import dayjs from 'dayjs'

import { TimeLocale } from '@app/hooks/contexts/timeController/timeControllerTypes'

import { history } from './store'
import { App } from './types/devices'
import { Task, TasksByDate } from './types/task'
import { SYSTEM_WEIGHT_STATE_TYPES } from './types/systemEvent'
import { TimelineByDate, TimelineItem } from './types/timeline'
import { CoachSystemAvailability } from './types/coachSystemData'
import { DarioSystemBGTargetRanges } from './types/darioSystemData'
import { PAGE_ERROR_401, PAGE_ERROR_400 } from './constants/navigation'

type ResizeObserverCallback = (
	entries: ResizeObserverEntry[],
	observer: ResizeObserver
) => void

export function resizeCallbackDebounceDecorator(
	resizeObserverCallback: ResizeObserverCallback,
	delay: number
): ResizeObserverCallback {
	let timeoutId: ReturnType<typeof setTimeout>

	return function (...args) {
		clearTimeout(timeoutId)

		timeoutId = setTimeout(
			// @ts-ignore
			() => resizeObserverCallback.apply(this, args),
			delay
		)
	}
}

enum APP_ERRORS {
	ERROR_400 = '400',
	ERROR_401 = '401',
	ERROR_403 = '403',
}

type ErrorRedirectAccess = {
	[key in APP_ERRORS]?: boolean
}

export const preciseRound = (num: number, decimals: number): number => {
	const sign = num >= 0 ? 1 : -1
	return +(
		Math.round(num * Math.pow(10, decimals) + sign * 0.001) /
		Math.pow(10, decimals)
	).toFixed(decimals)
}

export const executeResponseError = (
	e: any,
	fallbackErrorMsg?: string,
	errorRedirectAccess?: ErrorRedirectAccess
): string | undefined => {
	const defaultErrorsRedirectAccess: ErrorRedirectAccess = {
		'400': false,
		'401': true,
		'403': true,
	}

	const accessObject = errorRedirectAccess
		? { ...defaultErrorsRedirectAccess, ...errorRedirectAccess }
		: defaultErrorsRedirectAccess

	switch (e.response?.status) {
		case 400 || 404:
			accessObject?.[APP_ERRORS.ERROR_400] &&
				history.replace(PAGE_ERROR_400)

			return e.message || ''
		case 401 || 403: {
			;(accessObject?.[APP_ERRORS.ERROR_401] ||
				accessObject?.[APP_ERRORS.ERROR_403]) &&
				history.replace(PAGE_ERROR_401)

			return e.message || ''
		}
		default:
			return e.response?.data?.message || e.message || fallbackErrorMsg
	}
}

export const transformSysErrorMsgToUIMsg = (
	systemErrorMsg: string
): {
	type: 'info' | 'error' | 'success'
	msg: string
} => {
	if (systemErrorMsg === 'ERROR in search patient') {
		return {
			type: 'info',
			msg: `Sorry, you don't have any patients or searched patient doesn't exist.`,
		}
	}

	return {
		type: 'error',
		msg: systemErrorMsg,
	}
}

export const capitalizeFirstLetter = (val: string): string => {
	if (!val) return ''

	const lowerCase = val.toLowerCase()

	return lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1)
}

export const getNameOrMail = (
	firstName: string,
	lastName: string,
	email: string
): string => {
	let name = ''

	if (firstName && lastName) {
		name = `${firstName} ${lastName}`
	}

	if (firstName && !lastName) {
		name = firstName
	}

	if (!firstName && lastName) {
		name = lastName
	}

	if (!firstName && !lastName && email) {
		name = email
	}

	return name
}

export const transformAvailablePeriodsToArray = (
	availability: CoachSystemAvailability | undefined
): Array<string> => {
	const availablePeriods: Array<string> = []

	if (availability) {
		for (const option in availability) {
			if (availability[option]) {
				availablePeriods.push(option)
			}
		}
	}

	return availablePeriods
}

export const getTasksByDate = (tasks: Array<Task>): TasksByDate => {
	const tasksByDate = tasks.reduce((r, task) => {
		const today = dayjs(task.settings.dueDate).isToday()

		const dateKey = today ? 'Today' : task.settings.dueDate

		r[dateKey] = r[dateKey] || []

		r[dateKey].push(task)

		return r
	}, Object.create(null))

	return Object.entries(tasksByDate) as TasksByDate
}

export const getTimeLineByMonth = (
	timeline: Array<TimelineItem>
): TimelineByDate => {
	const lineByMonth = timeline.reduce((r, timelineItem) => {
		const monthKey = dayjs(timelineItem.created).format('MMM YYYY')
		r[monthKey] = r[monthKey] || []
		r[monthKey].push(timelineItem)
		return r
	}, Object.create(null))
	return Object.entries(lineByMonth)
}

export const getTimelineByDay = (
	timeline: Array<TimelineItem>
): TimelineByDate => {
	const lineByDay = timeline.reduce((r, timelineItem) => {
		const dayKey = dayjs(timelineItem.created).format('D MMM YYYY')
		r[dayKey] = r[dayKey] || []
		r[dayKey].push(timelineItem)
		return r
	}, Object.create(null))
	return Object.entries(lineByDay)
}

export const getMetricOrImperialHeight = (
	height: number,
	unit: string
): string | number => {
	const heightFloat = height ? parseFloat(height.toString()) : null

	if (heightFloat && unit === 'imperial') {
		const heightFt = heightFloat * 3.2808

		const inches = Math.round(
			parseFloat(
				(12 * (heightFt - parseInt(heightFt.toString()))).toString()
			)
		)

		return `${parseInt(heightFt.toString())}'${inches}`
	}

	if (heightFloat && unit === 'metric') {
		return preciseRound(heightFloat * 100, 0)
	}

	return ''
}

export const getMetricFromImperialHeight = (
	height: string | number
): number => {
	const splittedValue = height.toString().split(/'/g)

	const feetToMeter = splittedValue[0] ? +splittedValue[0] / 3.2808 : 0

	const inchToMeter = splittedValue[1] ? +splittedValue[1] / 39.37 : 0

	const resultMeters = feetToMeter + inchToMeter

	return preciseRound(resultMeters, 2) || 0
}

export const getMetricOrImperialWeight = (
	weight: number | null,
	unit: string
): number | string => {
	const floatWeight = weight ? parseFloat(weight.toString()) : null

	if (unit === 'imperial' && floatWeight) {
		const weightLbs = floatWeight * 2.2046

		return preciseRound(weightLbs, 1)
	}

	if (unit === 'metric' && floatWeight) {
		return preciseRound(floatWeight, 1)
	}

	return ''
}

export const getMetricWeightFromImperial = (weight: number): number => {
	const floatWeight = weight ? +weight : null

	if (floatWeight) {
		return weight / 2.2046
	}

	return 0
}

export const getMmollOrMgdlBGValue = (
	bgVal: number | null,
	unit: string
): number | null => {
	if (!bgVal) return null

	return unit === 'mgdl'
		? +preciseRound(bgVal * 18, 1)
		: preciseRound(bgVal, 1)
}

export const checkIsValidVersionApp = (
	currentApp: App | undefined
): boolean => {
	if (!currentApp) return false

	if (!currentApp?.version) return false

	const verValues = currentApp.version.split('.')

	return !(
		+verValues[0] < 4 ||
		(+verValues[0] === 4 && +verValues[1] < 4) ||
		(+verValues[0] === 4 && +verValues[1] < 4 && +verValues[2] < 2)
	)
}

export const latestUsedAppVersion = (apps: Array<App>): App | undefined => {
	const mobileApps = apps.filter(app =>
		['ios', 'android'].includes(app.platform.toLowerCase())
	)
	if (!mobileApps.length) return

	return mobileApps.reduce(function (p, v) {
		return dayjs(p.last_used).valueOf() > dayjs(v.last_used).valueOf()
			? p
			: v
	})
}

export const getAnyTitle = (title: string, prefix?: string): string => {
	if (prefix) {
		const newPrefix = title?.replace(/_/g, ' ').toLowerCase().split(' ')
		return newPrefix[0][0].toUpperCase() + newPrefix[0].substring(1) + '.'
	}
	let newTitle = title?.replace(/_/g, ' ').toLowerCase().split(' ')
	newTitle = newTitle.map(word => {
		return (word[0]?.toUpperCase() || '') + word.substring(1)
	})
	return newTitle.join(' ')
}

export const getWeightStatusInAccordanceBMIIndex = (
	BMIIndex: number
): string => {
	let weightStatus = ''

	if (BMIIndex < 18.5) weightStatus = SYSTEM_WEIGHT_STATE_TYPES.UNDERWEIGHT

	if (BMIIndex >= 18.5 && BMIIndex <= 24.9)
		weightStatus = SYSTEM_WEIGHT_STATE_TYPES.NORMAL

	if (BMIIndex >= 25 && BMIIndex <= 29.9)
		weightStatus = SYSTEM_WEIGHT_STATE_TYPES.OVERWEIGHT

	if (BMIIndex >= 30 && BMIIndex <= 34.9)
		weightStatus = SYSTEM_WEIGHT_STATE_TYPES.OBESE

	if (BMIIndex >= 35) weightStatus = SYSTEM_WEIGHT_STATE_TYPES.EXTREMELY_OBESE

	return weightStatus
}

export const getWeightStatusWithBMIIndex = (
	height: number,
	weight: number
): [number, string] => {
	const BMIIndex = weight / height ** 2

	const weightStatus = getWeightStatusInAccordanceBMIIndex(BMIIndex)

	return [BMIIndex, weightStatus]
}

export const getBMIIndexColor = (BMIIndex: number): string => {
	let colorThemeValue = 'primary'

	if (BMIIndex < 18.5) colorThemeValue = 'gold'

	if (BMIIndex >= 18.5 && BMIIndex <= 24.9) colorThemeValue = 'sushi'

	if (BMIIndex >= 25 && BMIIndex <= 29.9) colorThemeValue = 'steelBlue'

	if (BMIIndex >= 30 && BMIIndex <= 34.9) colorThemeValue = 'trueV'

	if (BMIIndex >= 35) colorThemeValue = 'temptress'

	return colorThemeValue
}

export const getNutrientsValue = (
	value: number | undefined,
	unit: string | undefined,
	roundValue?: number
): string => {
	if (!value || !unit) return ''

	if (unit === 'kg') {
		const carbsValue = +value * 1000

		const roundVal = roundValue !== 0 && !roundValue ? 2 : roundValue

		return `${preciseRound(carbsValue, roundVal)}`
	}

	return ''
}

export const getNutrientsUnit = (
	unit: string | undefined,
	additionalWord?: string
): string => {
	if (!unit) return ''

	if (unit === 'kg' && additionalWord) return `gr ${additionalWord}`

	if (unit === 'kg' && !additionalWord) return `gr`

	return ''
}

export const targetRangesForBGInMgOrMmol = (
	measureUnits: string,
	ranges?: DarioSystemBGTargetRanges
): DarioSystemBGTargetRanges => {
	const bgRanges: DarioSystemBGTargetRanges = {
		fasting: [
			measureUnits === 'mgdl' ? 80 : preciseRound(80 / 18, 1),
			measureUnits === 'mgdl' ? 120 : preciseRound(120 / 18, 1),
		],
		general: [
			measureUnits === 'mgdl' ? 80 : preciseRound(80 / 18, 1),
			measureUnits === 'mgdl' ? 120 : preciseRound(120 / 18, 1),
		],
		preMeal: [
			measureUnits === 'mgdl' ? 80 : preciseRound(80 / 18, 1),
			measureUnits === 'mgdl' ? 120 : preciseRound(120 / 18, 1),
		],
		postMeal: [
			measureUnits === 'mgdl' ? 80 : preciseRound(80 / 18, 1),
			measureUnits === 'mgdl' ? 120 : preciseRound(120 / 18, 1),
		],
		bedtime: [
			measureUnits === 'mgdl' ? 80 : preciseRound(80 / 18, 1),
			measureUnits === 'mgdl' ? 120 : preciseRound(120 / 18, 1),
		],
		hyper: measureUnits === 'mgdl' ? 200 : preciseRound(200 / 18, 1),
		hypo: measureUnits === 'mgdl' ? 60 : preciseRound(60 / 18, 1),
	}

	if (!ranges) return bgRanges

	for (const target in ranges) {
		if (ranges[target] instanceof Array) {
			bgRanges[target] = (ranges[target] as Array<number>).map(item =>
				measureUnits === 'mgdl'
					? preciseRound(item * 18, 0)
					: preciseRound(item, 1)
			)
		} else if (typeof ranges[target] === 'number') {
			const targetValue = ranges[target] as number

			bgRanges[target] =
				measureUnits === 'mgdl'
					? preciseRound(targetValue * 18, 0)
					: preciseRound(targetValue, 1)
		}
	}

	return bgRanges
}

export const targetBGRangesFromMgToMmol = (
	ranges: DarioSystemBGTargetRanges
): DarioSystemBGTargetRanges => {
	const bgRanges = {} as DarioSystemBGTargetRanges

	for (const target in ranges) {
		if (ranges[target] instanceof Array) {
			bgRanges[target] = (ranges[target] as Array<number>).map(item =>
				preciseRound(item * 0.0555, 2)
			)
		} else if (typeof ranges[target] === 'number') {
			const targetValue = ranges[target] as number

			bgRanges[target] = preciseRound(targetValue * 0.0555, 2)
		}
	}

	return bgRanges
}

export const getBloodPressureRange = (
	sys: number | null,
	dia: number | null
): string => {
	if (!sys || !dia) return 'empty'
	else if (sys > 180 || dia > 120) return 'hyper_bp'
	else if (sys > 139 || dia > 89) return 'high_stg_2'
	else if ((sys > 129 && dia < 140) || (sys >= 80 && dia < 90))
		return 'high_stg_1'
	else if (sys > 90 && sys < 120 && dia < 80) return 'normal'
	else if (sys >= 120 && sys < 130 && dia < 80) return 'elevated'
	else if (sys < 90 || dia < 60) return 'low'
	else return 'normal'
}

export const createSearchRegex = (value: string): RegExp => {
	const tokens = value
		.toString()
		.toLowerCase()
		.split(' ')
		.filter(token => token.trim() !== '')

	return new RegExp(tokens.join('\\|'), 'gim')
}

export const getEnumValueUsingKeyString = (
	value: string,
	enumValue: object
): string => {
	return enumValue[value as keyof typeof enumValue]
}

export const getLabelWithSelectedItemsCount = (
	label: string,
	selectedCount: number,
	maxCount: number
): string => {
	const countLabel =
		selectedCount === maxCount
			? ' (all)'
			: ` (${selectedCount}/${maxCount})`

	return `${label}${selectedCount ? countLabel : ''}`
}

export const getUsersTimeLocation = (dateConfig?: string): TimeLocale =>
	dateConfig === 'DD/MM/YY' ? 'eu' : 'us'

export const calculateStandardDeviation = (arr: Array<number>): number => {
	if (arr.length <= 1) return 0
	const mean = arr.reduce((a: number, b: number) => a + b, 0) / arr.length
	const variance =
		arr.reduce((a: number, b: number) => a + (b - mean) ** 2, 0) /
		(arr.length - 1)
	return Math.round(Math.sqrt(variance))
}

export const calculateAverageArrayValue = (arr: Array<number>): number => {
	if (arr.length < 1) return 0
	const value = arr.reduce((p: number, c: number) => p + c, 0) / arr.length
	return Math.round(value)
}

export const getSmallestArrayValue = (arr: Array<number>): number => {
	if (arr.length < 1) return 0
	return Math.round(Math.min(...arr))
}

export const getBiggestArrayValue = (arr: Array<number>): number => {
	if (arr.length < 1) return 0
	return Math.round(Math.max(...arr))
}
